/**
 * HoursStep — Step 2: Stepper + PhaseAccumulator for hours.
 * Uses quantity-based PhaseAccumulator (toggle behavior: increment sets to 1, clear removes).
 */

import { T } from '@/styles/tokens';
import type { MediumKey } from '@/types/medium';
import { PHASES } from '@/constants/phases';
import { StepperControl } from './StepperControl';
import { PhaseAccumulator } from './PhaseAccumulator';
import { StepNextButton } from './StepNextButton';
import { SectionIcon } from './SectionIcons';

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 16,
  border: `1px solid ${T.cream200}`,
  padding: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

interface HoursStepProps {
  hours: number;
  medium: MediumKey;
  phaseQuantities: Map<number, number>;
  onChangeHours: (h: number) => void;
  onIncrementPhase: (idx: number, hrs: number) => void;
  onDecrementPhase: (idx: number, hrs: number) => void;
  onClearPhase: (idx: number, totalHrs: number) => void;
  onResetPhases: () => void;
  onNext: () => void;
}

export function HoursStep({
  hours, medium, phaseQuantities, onChangeHours,
  onIncrementPhase, onDecrementPhase, onClearPhase, onResetPhases, onNext,
}: HoursStepProps) {
  const phases = PHASES[medium] || PHASES.oil;
  const phaseItems = phases.map((p) => ({ label: p.label, value: p.hrs }));

  return (
    <div>
      <SectionIcon icon="clock" label="Hours" />
      <div style={cardStyle}>
        <StepperControl
          value={hours > 0 ? String(hours) : '\u2014'}
          onDecrement={() => onChangeHours(Math.max(0, hours - 1))}
          onIncrement={() => onChangeHours(hours + 1)}
        />
        <PhaseAccumulator
          items={phaseItems}
          quantities={phaseQuantities}
          onIncrement={onIncrementPhase}
          onDecrement={onDecrementPhase}
          onClearItem={onClearPhase}
          onReset={onResetPhases}
          unit="hrs"
        />
        <StepNextButton label="Next" disabled={hours <= 0} onPress={onNext} />
      </div>
    </div>
  );
}
